import type { OrganizationUser } from '@compass/client/types/organization_user';
import { Loader2, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useOrganizationTeams } from '@/hooks/useTeams';
import { useSetUserSkills, useUserSkills } from '@/hooks/useUserSkills';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  getAvailableSkillOptions,
  SKILL_ROLES,
  skillLabel,
  SkillRole,
  skillRole,
  skillRoleFlags,
  skillRoleLabel,
} from '@/utils/skills';

import { usersAlreadyOnATeam } from './teamMembership';

interface SkillEntry {
  skill: string;
  role: SkillRole;
}

interface ManageSkillsDialogProps {
  organizationId: string;
  user: OrganizationUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Segmented Backup/Primary/Leader toggle for a skill. All options are always
 * visible and the selected one is highlighted. Marking a user as Leader makes
 * them the single leader for the skill in the org; the backend automatically
 * demotes any previous leader.
 */
const SkillRoleToggle: React.FC<{ role: SkillRole; onChange: (role: SkillRole) => void }> = ({
  role,
  onChange,
}) => (
  <div className="inline-flex items-center rounded-full bg-muted p-0.5 text-xs font-medium">
    {SKILL_ROLES.map(value => {
      const active = role === value;
      return (
        <button
          key={value}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(value)}
          className={cn(
            'rounded-full px-3 py-1 transition-colors',
            active
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {skillRoleLabel(value)}
        </button>
      );
    })}
  </div>
);

/**
 * Org-admin dialog to manage a user's skills within an organization.
 * Loads the existing skill set, lets the admin add/remove skills and toggle
 * each between primary and backup, then submits the full set (SetUserSkills
 * has full-replace semantics).
 */
export const ManageSkillsDialog: React.FC<ManageSkillsDialogProps> = ({
  organizationId,
  user,
  open,
  onOpenChange,
}) => {
  const { data: skillsData, isLoading } = useUserSkills(organizationId, user?.user_id);
  const setUserSkills = useSetUserSkills();
  const [entries, setEntries] = useState<SkillEntry[]>([]);

  // A user must belong to a team IN THIS ORG before skills can be assigned to them in it
  // (backend invariant; skills are org-scoped — BI-1643). Reuse the same "is this user on
  // any team" helper the team pickers use for the opposite check (excludeTeamId omitted here
  // on purpose); it is fed the current org's teams, which is exactly the right scope.
  const { teams, isLoading: isLoadingTeams } = useOrganizationTeams(organizationId);
  const teamedUserIds = useMemo(() => usersAlreadyOnATeam(teams), [teams]);
  const userOnATeam = !!user?.user_id && teamedUserIds.has(user.user_id);
  // Don't flag a legitimate on-team user as "not on a team" while the teams
  // query is still loading (teams=[] initially would otherwise false-positive).
  const showNoTeamBlock = !isLoadingTeams && !userOnATeam;

  // Initialise local editing state from the server whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setEntries(
        (skillsData?.skills || []).map(s => ({ skill: s.skill || '', role: skillRole(s) })),
      );
    }
  }, [open, skillsData]);

  const availableOptions = useMemo(
    () => getAvailableSkillOptions(entries.map(e => e.skill)),
    [entries],
  );

  // Block Save only when the desired set is non-empty and the user isn't on a
  // team — a user with pre-existing skills but no team should still be able to
  // clear them out.
  const blockSaveForNoTeam = entries.length > 0 && showNoTeamBlock;

  // Skill leader ⇒ team lead (BI-1698): the Leader role requires this user to be the lead
  // (manager) of a team in this org. Mirrors the backend assertSkillLeaderIsTeamLead guard so
  // the admin sees it before saving. The rule is one-directional and workflow-type agnostic —
  // a team lead may hold no leadership at all, and any skill may be led. A user leads at most
  // one team per org (BI-1643), and `teams` is already scoped to this org.
  const managedTeam = useMemo(
    () => (teams || []).find(t => t.manager_id && t.manager_id === user?.user_id),
    [teams, user?.user_id],
  );
  // Don't block while teams are still loading (teams=[] initially would false-positive).
  const leaderRequiresTeamLead =
    !isLoadingTeams && !managedTeam && entries.some(e => e.role === SkillRole.Leader);

  const addSkill = (value: string | boolean | number) => {
    if (showNoTeamBlock) return;
    const skill = String(value);
    if (!skill) return;
    setEntries(prev =>
      prev.some(e => e.skill === skill) ? prev : [...prev, { skill, role: SkillRole.Backup }],
    );
  };

  const removeSkill = (skill: string) => {
    setEntries(prev => prev.filter(e => e.skill !== skill));
  };

  const setRole = (skill: string, role: SkillRole) => {
    setEntries(prev => prev.map(e => (e.skill === skill ? { ...e, role } : e)));
  };

  const handleSave = async () => {
    if (!user?.user_id) return;
    try {
      await setUserSkills.mutateAsync({
        organization_id: organizationId,
        user_id: user.user_id,
        skills: entries.map(e => ({ skill: e.skill, ...skillRoleFlags(e.role) })),
      });
      toast.success('Skills updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update skills'));
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader className="space-y-3 mb-2">
          <div>
            <DialogTitle className="text-xl">Manage Skills</DialogTitle>
            <DialogDescription>
              Assign workflow skills for {user.name || user.email}. Mark a skill as primary if this
              user is a preferred handler, or leader to make them the single lead for that skill in
              the organization — promoting a leader replaces the current one.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium">Add a skill</p>
            <SearchableSelect
              value=""
              onValueChange={addSkill}
              options={availableOptions}
              placeholder="Select a skill to add..."
              searchPlaceholder="Search skills..."
              emptyText="No more skills to add."
              disabled={isLoading || availableOptions.length === 0 || showNoTeamBlock}
            />
            {showNoTeamBlock && (
              <p className="mt-2 text-sm text-destructive">
                This user must be added to a team before they can be assigned skills.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Assigned skills</p>
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading skills...
              </div>
            ) : entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No skills assigned yet.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {entries.map(entry => (
                  <li key={entry.skill} className="flex items-center justify-between gap-3 p-3">
                    <span className="text-sm font-medium">{skillLabel(entry.skill)}</span>
                    <div className="flex items-center gap-3">
                      <SkillRoleToggle
                        role={entry.role}
                        onChange={role => setRole(entry.skill, role)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeSkill(entry.skill)}
                        aria-label={`Remove ${skillLabel(entry.skill)}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {leaderRequiresTeamLead && (
            <p className="text-sm text-destructive">
              Only a team lead can be a skill leader. Make this user the lead of a team in this
              organization, or set their skills to Primary or Backup.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={setUserSkills.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              isLoading || setUserSkills.isPending || blockSaveForNoTeam || leaderRequiresTeamLead
            }
          >
            {setUserSkills.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
